import {
  NICE_CEIL_CONFIG,
  TIMELINE_CHART_CONFIG,
  TIMELINE_CHART_HEIGHT,
} from '../constants/analytics.constants';
import { TimelineChartGeometry, TimelinePoint } from '../types/analytics.types';
import { maxTimelineValue } from './analytics.utils';

const MIN_POINT_COUNT = 1;
const MIN_Y_AXIS_RANGE = 1;
const LOG_BASE_10 = 10;

/**
 * Returns the smallest "nice" number (1/2/5 × power of 10) greater than or equal to value.
 */
export const niceCeil = (value: number): number => {
  if (value <= NICE_CEIL_CONFIG.SMALL_CEIL) return NICE_CEIL_CONFIG.SMALL_CEIL;
  if (value <= NICE_CEIL_CONFIG.MEDIUM_CEIL) return NICE_CEIL_CONFIG.MEDIUM_CEIL;
  const magnitude = Math.pow(LOG_BASE_10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  const step = NICE_CEIL_CONFIG.STEPS.find((s) => normalized <= s) ?? NICE_CEIL_CONFIG.STEPS[NICE_CEIL_CONFIG.STEPS.length - 1];
  return step * magnitude;
};

export const buildTimelineGeometry = (points: TimelinePoint[]): TimelineChartGeometry => {
  const {
    WIDTH,
    PADDING_TOP,
    PADDING_RIGHT,
    PADDING_BOTTOM,
    PADDING_LEFT,
    Y_AXIS_TICK_COUNT,
    BAR_WIDTH_MIN_PX,
    BAR_WIDTH_MAX_PX,
    BAR_WIDTH_RATIO,
  } = TIMELINE_CHART_CONFIG;

  const padding = {
    top: PADDING_TOP,
    right: PADDING_RIGHT,
    bottom: PADDING_BOTTOM,
    left: PADDING_LEFT,
  };

  const innerWidth = WIDTH - padding.left - padding.right;
  const innerHeight = TIMELINE_CHART_HEIGHT - padding.top - padding.bottom;
  const count = Math.max(points.length, MIN_POINT_COUNT);
  const barGroupWidth = innerWidth / count;
  const barWidth = Math.max(
    BAR_WIDTH_MIN_PX,
    Math.min(BAR_WIDTH_MAX_PX, barGroupWidth * BAR_WIDTH_RATIO),
  );
  const rawMax = maxTimelineValue(points);
  const yMax = niceCeil(Math.max(rawMax, MIN_Y_AXIS_RANGE));
  const yTicks: number[] = [];
  for (let i = 0; i <= Y_AXIS_TICK_COUNT; i += 1) {
    yTicks.push(Math.round((yMax * i) / Y_AXIS_TICK_COUNT));
  }

  return {
    width: WIDTH,
    height: TIMELINE_CHART_HEIGHT,
    padding,
    innerWidth,
    innerHeight,
    barGroupWidth,
    barWidth,
    yMax,
    yTicks,
  };
};

export const hasAnyTimelineActivity = (points: TimelinePoint[]): boolean =>
  points.some((p) => p.created + p.updated > 0);
